import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Database, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthAPI } from "@/lib/api";

/**
 * OnboardingDialog — shown to brand new users on first login.
 * Non-dismissible. Two choices:
 *   demo  → backend seeds a realistic Hyundai/Toyota/Honda dataset
 *   fresh → empty account, user starts from zero
 */
export default function OnboardingDialog({ open, userName, onDone }) {
  const [submitting, setSubmitting] = useState(null); // 'demo' | 'fresh' | null
  const [hasFired, setHasFired] = useState(false); // Rigorous double-trigger guard

  const pick = async (mode) => {
    // If we are already loading or have successfully triggered onDone, exit immediately
    if (submitting || hasFired) return;
    
    setSubmitting(mode);
    try {
      const res = await AuthAPI.submitOnboarding(mode);
      
      // Lock the dialog permanently before executing parent state transitions
      setHasFired(true); 
      
      if (mode === "demo") {
        toast.success(
          `Demo data loaded — ${res.vehicles || 3} vehicles, ${res.trips || 70} trips`,
        );
      } else {
        toast.success("Starting fresh — your garage is empty");
      }
      
      // Transition out
      await onDone();
    } catch (e) {
      // Only show error toasts if a genuine initial failure occurred
      if (!hasFired) {
        const msg = e?.response?.data?.detail || "Could not complete onboarding";
        toast.error(msg);
        setSubmitting(null);
      }
    }
  };

  return (
    <Dialog open={open && !hasFired}>
      <DialogContent
        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] sm:max-w-[600px] p-0 overflow-hidden [&>button]:hidden"
        data-testid="onboarding-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="px-7 pt-7 pb-2">
          <div className="label-tiny mb-2">Welcome{userName ? `, ${userName.split(" ")[0]}` : ""}</div>
          <DialogTitle className="font-display text-3xl font-light tracking-tight text-[var(--text-primary)] leading-tight">
            How would you like to begin?
          </DialogTitle>
          <p className="text-[var(--text-secondary)] mt-2 text-sm leading-relaxed">
            You can explore the dashboard with sample vehicles and trips, or start
            with an empty garage and add your own from scratch.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-7 pt-4 pb-7">
          {/* Demo Option */}
          <button
            type="button"
            disabled={submitting != null || hasFired}
            onClick={() => pick("demo")}
            data-testid="onboarding-demo-btn"
            className="group text-left bg-[var(--bg-elevated)] border border-[var(--border-subtle)] hover:border-[#E82127] p-5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex flex-col rounded-[4px] min-h-[220px]"
          >
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 flex items-center justify-center bg-[var(--bg-emphasis)] border border-[var(--border-subtle)] group-hover:border-[#E82127]/40 transition-colors rounded-full">
                <Sparkles className="w-4 h-4 text-[var(--text-primary)] group-hover:text-[#E82127] transition-colors" strokeWidth={1.75} />
              </div>
              {submitting === "demo" && (
                <Loader2 className="w-4 h-4 animate-spin text-[#E82127]" strokeWidth={2} />
              )}
            </div>
            <div className="mt-4 flex-1">
              <div className="font-display text-lg font-light tracking-tight text-[var(--text-primary)]">
                Load demo data
              </div>
              <p className="text-[12px] text-[var(--text-secondary)] leading-relaxed mt-1.5">
                3 sample vehicles — Hyundai Ioniq 5 (EV), Toyota RAV4 Hybrid, Honda
                Civic (gas) — with 9 months of realistic trips and energy entries.
              </p>
            </div>
            <div className="label-tiny mt-3 text-[var(--text-faint)] group-hover:text-[#E82127] transition-colors">
              Explore the dashboard →
            </div>
          </button>

          {/* Fresh Option */}
          <button
            type="button"
            disabled={submitting != null || hasFired}
            onClick={() => pick("fresh")}
            data-testid="onboarding-fresh-btn"
            className="group text-left bg-[var(--bg-elevated)] border border-[var(--border-subtle)] hover:border-white/50 p-5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex flex-col rounded-[4px] min-h-[220px]"
          >
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 flex items-center justify-center bg-[var(--bg-emphasis)] border border-[var(--border-subtle)] group-hover:border-white/40 transition-colors rounded-full">
                <Database className="w-4 h-4 text-[var(--text-primary)]" strokeWidth={1.75} />
              </div>
              {submitting === "fresh" && (
                <Loader2 className="w-4 h-4 animate-spin text-[var(--text-primary)]" strokeWidth={2} />
              )}
            </div>
            <div className="mt-4 flex-1">
              <div className="font-display text-lg font-light tracking-tight text-[var(--text-primary)]">
                Start fresh
              </div>
              <p className="text-[12px] text-[var(--text-secondary)] leading-relaxed mt-1.5">
                Begin with an empty garage. Add your own vehicles, log trips manually
                or import a CSV/XLSX of your existing mileage.
              </p>
            </div>
            <div className="label-tiny mt-3 text-[var(--text-faint)] group-hover:text-[var(--text-primary)] transition-colors">
              I'll add my own →
            </div>
          </button>
        </div>

        <div className="px-7 pb-6 -mt-2 text-[11px] text-[var(--text-faint)] leading-relaxed">
          You can clear or replace data at any time from the Vehicles page.
        </div>
      </DialogContent>
    </Dialog>
  );
}